'use client'

import { useTranslations } from 'next-intl'

import { Dialog } from '@/shared/ui'

import { usePassportOCR } from '../../api/passport-ocr'
import { useValidatePhoneNumber } from '../../api/validate-phone-number'
import { TPassportVerificationForm } from '../../model/passport-verification-form'
import { PassportContent } from '../passport-content'

export const PASSPORT_VERIFICATION_DIALOG = 'PASSPORT_VERIFICATION'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: (props: {
    formValues: TPassportVerificationForm
    ocrToken?: string
  }) => void
  onMaxFail: (props: {
    formValues: TPassportVerificationForm
    ocrToken?: string
  }) => void
  defaultValues?: Partial<TPassportVerificationForm>
  emailCertified?: boolean
}

export const PassportDialog = ({
  isOpen,
  onClose,
  onSuccess,
  onMaxFail,
  defaultValues,
  emailCertified,
}: Props) => {
  const t = useTranslations('passport_verification')

  const { validatePhoneNumberAsync } = useValidatePhoneNumber()
  const { requestOcrAsync, isScanning } = usePassportOCR()

  const submitForm = async (formValues: TPassportVerificationForm) => {
    const { contactNumber, passportImageUrl } = formValues

    const initialContactNumber = defaultValues?.contactNumber

    if (initialContactNumber !== contactNumber) {
      const isValidPhoneNumber = await validatePhoneNumberAsync(contactNumber)

      if (!isValidPhoneNumber) return
    }

    if (!emailCertified) {
      onSuccess({ formValues })

      return
    }

    await requestOcrAsync(passportImageUrl, {
      onSuccess: ({ ocrToken }) => {
        onSuccess({
          formValues,
          ocrToken,
        })
      },
      onMaxFail: () => {
        onMaxFail({
          formValues,
          ocrToken: '',
        })
      },
    })
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="h-[690px] gap-0 p-0">
        <PassportContent
          isScanning={isScanning}
          defaultValues={defaultValues}
          onClose={onClose}
          onSubmit={submitForm}
          submitText={
            emailCertified
              ? t('button.complete')
              : t('button.complete_registration')
          }
        />
      </Dialog.Content>
    </Dialog.Root>
  )
}
